import { NoteAnchor, NoteEntry, NoteMention, NoteReply } from '../components/account-notes/account-notes.types';
import { NAME_CHAR, escapeRegExp } from '../components/mention-input/mention-text';
import { NoteDetail, NoteDetailFact } from '../components/note-detail/note-detail.types';
import { NoteRow, NoteSort, NoteSortKey } from '../components/notes-overview/notes-overview.types';
import { IClosingNote, IClosingNoteReply } from '../services/closing-note.service';

/**
 * Die zitierte Stelle für die Notiz-Ansichten — REV-21369.
 *
 * ⚠️ Die Ansichten bekommen NIE Geometrie (ADR-0019 E5 gilt für sie weiter): nur Zitat, Block und
 * — bei einer Markierung im Beleg — die Seite. Eine Flächen-Markierung hat kein Zitat; dann trägt
 * `snippet` einen Leerstring und die Ansicht schreibt „Markierte Fläche · Seite n".
 *
 * ⚠️ `documentId` NUR mit Fundstelle im Beleg (N4): ein Dokument-Kommentar ohne Markierung hat nichts,
 * wohin der Chip springen könnte, und bleibt deshalb unklickbar.
 */
export function toNoteAnchor(anchor: IClosingNote['anchor'], documentId?: string | null): NoteAnchor | null {
    if (anchor === null || anchor === undefined) {
        return null;
    }
    return {
        snippet: anchor.snippet ?? '',
        blockId: anchor.blockId ?? null,
        page: anchor.pdf ? anchor.pdf.page : null,
        documentId: anchor.pdf && documentId ? documentId : null,
    };
}

//REV-21374 — die Uebersetzung zwischen Speicher und Ansicht.
//
//⚠️ ALLES, was die Pure Views nicht tun, passiert hier: sortieren, filtern, suchen, Datum
//formatieren, „ueberfaellig" entscheiden, das Erwaehnungs-Token zerlegen und die CSV bauen. Das ist
//Absicht (ADR-0019, conventions.md §ABCP-FE) — und es ist AUCH der Grund, warum Tabelle und Export
//dieselbe Antwort geben: beide nehmen denselben Weg durch diese Datei.

export interface NoteUserLookup {
    /** Anzeigename je userId. Fehlt einer, bleibt die Kennung sichtbar statt zu verschwinden. */
    nameOf(userId: string): string | null;
}

/**
 * Kontonummer und -bezeichnung, GETRENNT.
 *
 * ⚠️ Getrennt und nicht als ein Label: die Tabelle setzt die Nummer monospace über den Namen,
 * damit die Nummern spaltenweise vergleichbar bleiben — zusammengeklebt geht genau das verloren.
 * `null` ⇒ das Konto gehört nicht (mehr) zum Abschluss.
 */
export interface NoteAccountLookup {
    numberOf(accountId: string): string | null;
    nameOf(accountId: string): string | null;
}

/**
 * Das Speicherformat einer Erwaehnung: `@[Anzeigename|userId]` (ADR-0019 E3).
 *
 * ⚠️ Global, aber die Funktion baut ihn bei jedem Aufruf NEU. Ein `/g`-Ausdruck traegt `lastIndex`
 * zwischen Aufrufen mit — derselbe Fehler, der in REV-21365 die Hervorhebung zerlegt hat (SRR F1).
 */
function mentionPattern(): RegExp {
    return /@\[([^|\]]+)\|([^\]]+)\]/g;
}

/** Zerlegt das Speicherformat in Anzeigetext + die erwaehnten Personen. */
export function decodeMentions(stored: string): { text: string; mentions: NoteMention[] } {
    const mentions: NoteMention[] = [];
    const text = stored.replace(mentionPattern(), (_match, displayName: string, userId: string) => {
        mentions.push({ userId, displayName });
        return `@${displayName}`;
    });
    return { text, mentions };
}

/** Baut das Speicherformat aus Klartext + den gewaehlten Personen. */
export function encodeMentions(text: string, users: readonly { userId: string; displayName: string }[]): string {
    let out = text;
    // Laengste zuerst: sonst verschluckt „Anna Meier" den Namen „Anna Meier-Brun".
    const sorted = [...users].sort((a, b) => b.displayName.length - a.displayName.length);
    for (const user of sorted) {
        // Only the whole name: `@Anna Meier` inside `@Anna Meierhans` stays text.
        const name = new RegExp(`@${escapeRegExp(user.displayName)}(?!${NAME_CHAR.source})`, 'g');
        out = out.replace(name, () => `@[${user.displayName}|${user.userId}]`);
    }
    return out;
}

/** The people picked in the field — only their names become mentions, a typed name stays text. */
export function mentionedUsers<T extends { userId: string }>(users: readonly T[], userIds: readonly string[]): T[] {
    return users.filter((user) => userIds.includes(user.userId));
}

/**
 * Initialen fuer den Avatar.
 *
 * ⚠️ Ohne diese Funktion stand im Kreis ein `?`: die Ansicht liest `author.initials`, und der
 * Presenter hat das Feld nie gesetzt. Der Kreis war also da und beschriftet mit nichts.
 */
export function initialsOf(name: string | null | undefined): string | null {
    if (!name) {
        return null;
    }
    const parts = name.trim().split(/\s+/).filter((part) => part.length > 0);
    if (parts.length === 0) {
        return null;
    }
    const first = parts[0].charAt(0);
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
    return (first + last).toUpperCase();
}

/** Schweizer Notation, ohne Bibliothek — `31.12.2026`. */
export function swissDate(iso: string | null): string | null {
    if (iso === null) {
        return null;
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return null;
    }
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    return `${dd}.${mm}.${date.getFullYear()}`;
}

/**
 * ⚠️ **Eine erledigte Pendenz ist NIE ueberfaellig** (AC6).
 *
 * Die Regel steht HIER, damit Tabelle, Detailansicht und CSV dieselbe Antwort geben. Sie in der
 * Ansicht zu rechnen hiesse, sie dreimal zu rechnen — und in der Zeitzone des Browsers statt in
 * der des Mandats.
 */
export function isOverdue(note: IClosingNote, now: Date = new Date()): boolean {
    if (note.kind !== 'TODO' || note.doneAt !== null || note.dueAt === null) {
        return false;
    }
    const due = new Date(note.dueAt);
    return !Number.isNaN(due.getTime()) && due.getTime() < now.getTime();
}

// ── Abhaken mit Frist ───────────────────────────────────────────────────────────────

// ── Zeile der Uebersicht ──────────────────────────────────────────────────────────────────────

export function toRow(note: IClosingNote, users: NoteUserLookup, accounts: NoteAccountLookup): NoteRow {
    const decoded = decodeMentions(note.text);
    const authorName = users.nameOf(note.authorId);
    return {
        id: note._id,
        kind: note.kind,
        text: decoded.text,
        tags: note.tags as NoteRow['tags'],
        source: note.source,
        accountNumber: note.accountId === null ? null : accounts.numberOf(note.accountId),
        accountName: note.accountId === null ? null : accounts.nameOf(note.accountId),
        accountId: note.accountId,
        author: { type: 'user', name: authorName },
        createdLabel: swissDate(note.createdAt) ?? '',
        dueLabel: swissDate(note.dueAt),
        overdue: isOverdue(note),
        done: note.doneAt !== null,
        assignee:
            note.kind === 'TODO' && note.assigneeId !== null
                ? { id: note.assigneeId, name: users.nameOf(note.assigneeId) }
                : null,
    };
}

// ── Eintrag des Konto-Bereichs ────────────────────────────────────────────────────────────────

function toReply(reply: IClosingNoteReply, users: NoteUserLookup): NoteReply {
    const decoded = decodeMentions(reply.text);
    return {
        id: reply.id,
        author: (() => {
            const name = users.nameOf(reply.authorId);
            return { type: 'user' as const, name, initials: initialsOf(name) };
        })(),
        timeLabel: swissDate(reply.at) ?? '',
        text: decoded.text,
        mentions: decoded.mentions,
    };
}

export function toEntry(note: IClosingNote, users: NoteUserLookup, currentUserId: string | null): NoteEntry {
    const decoded = decodeMentions(note.text);
    const isAuthor = note.authorId === currentUserId;
    const authorName = users.nameOf(note.authorId);
    const replies = note.replies.map((r) => toReply(r, users));
    const last = replies.length > 0 ? replies[replies.length - 1] : null;
    return {
        id: note._id,
        kind: note.kind,
        source: note.source,
        author: { type: 'user', name: authorName, initials: initialsOf(authorName) },
        timeLabel: swissDate(note.createdAt) ?? '',
        text: decoded.text,
        mentions: decoded.mentions,
        tags: note.tags as NoteEntry['tags'],
        anchor: toNoteAnchor(note.anchor, note.documentId),
        replies,
        replyCount: replies.length,
        lastReplyTimeLabel: last ? last.timeLabel : null,
        done: note.doneAt !== null,
        // ⚠️ Die Ansicht entscheidet die Rechte NICHT — sie liest sie hier ab, und hier stehen sie
        // als Spiegel der Serverregel (ADR-0019 E9). Der Server bleibt die Schranke.
        canEdit: isAuthor,
        canDelete: isAuthor,
    };
}

// ── Detailansicht ─────────────────────────────────────────────────────────────────────────────

export interface DetailLabels {
    status: string;
    tags: string;
    assignee: string;
    account: string;
    created: string;
    author: string;
    due: string;
    statusOpen: string;
    statusDone: string;
    tagLabel(tag: string): string;
}

/**
 * REV-21369 — eine Notiz AM BELEG trägt das Konto ihres Belegs (ADR-0021 E4); der Server weist einen
 * Kontowechsel ab (SRR F1). Das Feld anzubieten hiesse, einen Weg anzubieten, der immer mit einem
 * Fehler endet (Code-Review R1).
 */
function hasDocument(note: IClosingNote): boolean {
    return note.documentId !== null && note.documentId !== undefined;
}

export function toDetail(
    note: IClosingNote,
    users: NoteUserLookup,
    accounts: NoteAccountLookup,
    currentUserId: string | null,
    isOrgAdmin: boolean,
    labels: DetailLabels,
): NoteDetail {
    const decoded = decodeMentions(note.text);
    const isAuthor = note.authorId === currentUserId;
    const isTodo = note.kind === 'TODO';
    const accountLabel =
        note.accountId === null
            ? null
            : [accounts.numberOf(note.accountId) ?? '', accounts.nameOf(note.accountId) ?? '']
                  .filter((x) => x.length > 0)
                  .join(' · ') || null;

    // ⚠️ `value: null` heisst „die Zeile erscheint GAR NICHT" (AC3). Ein leeres „Faellig" an einem
    // Kommentar behauptet ein Feld, das es an einem Kommentar nicht gibt.
    const facts: NoteDetailFact[] = [
        {
            key: 'status',
            label: labels.status,
            value: isTodo ? (note.doneAt !== null ? labels.statusDone : labels.statusOpen) : null,
        },
        { key: 'tags', label: isTodo ? labels.tags : labels.tags, value: note.tags.map(labels.tagLabel).join(', ') },
        {
            key: 'assignee',
            label: labels.assignee,
            value: isTodo && note.assigneeId !== null ? users.nameOf(note.assigneeId) : null,
        },
        { key: 'account', label: labels.account, value: accountLabel },
        { key: 'created', label: labels.created, value: swissDate(note.createdAt) },
        { key: 'author', label: labels.author, value: users.nameOf(note.authorId) },
        { key: 'due', label: labels.due, value: isTodo ? swissDate(note.dueAt) : null },
    ];

    return {
        id: note._id,
        kind: note.kind,
        source: note.source,
        text: decoded.text,
        description: note.description,
        // Der Rohwert für das Datumsfeld: `2026-09-08` aus `2026-09-08T00:00:00.000Z`.
        dueValue: note.dueAt === null ? null : note.dueAt.slice(0, 10),
        tags: note.tags as NoteDetail['tags'],
        author: (() => {
            const name = users.nameOf(note.authorId);
            return { type: 'user' as const, name, initials: initialsOf(name) };
        })(),
        anchor: toNoteAnchor(note.anchor, note.documentId),
        accountLabel,
        accountId: note.accountId,
        assigneeId: isTodo ? note.assigneeId : null,
        facts,
        replies: note.replies.map((r) => toReply(r, users)),
        done: note.doneAt !== null,
        canEdit: isAuthor,
        // ⚠️ Die einzige Handlung mit zwei Berechtigten (AC5).
        canDelete: isAuthor || isOrgAdmin,
        // ⚠️ Titel, Erläuterung und Marken gibt JEDER Eintrag frei, den sein Verfasser ändern
        // darf. Das Konto ebenfalls — es war einmal unveränderlich, aber eine am falschen Konto
        // erfasste Pendenz gehört verschoben und nicht gelöscht-und-neu-geschrieben.
        editableFields: (!isAuthor
            ? []
            : isTodo
            ? // ⚠️ Die Fälligkeit gibt JEDE Pendenz frei, auch die am Konto. AC6 sah hier weniger
              // vor („eine Konto-Pendenz gibt weniger frei als eine Mandats-Pendenz“), aber eine
              // Frist ist die Eigenschaft, die man an einer Pendenz am häufigsten korrigiert — und
              // sie war ausgerechnet dort nicht änderbar, wo die meisten Pendenzen entstehen.
              // Bewusst abweichend, auf Ansage beim Durchsehen.
              //
              // «Zugewiesen an» for EVERY Pendenz, the account's too: it is now set when
              // creating one on the account page, so it must be changeable where it is shown.
              ['text', 'description', 'tags', 'account', 'due', 'assignee']
            : ['text', 'description', 'tags']
        ).filter((field) => field !== 'account' || !hasDocument(note)) as NoteDetail['editableFields'],
    };
}

// ── Filtern, Suchen, Sortieren ────────────────────────────────────────────────────────────────

export interface OverviewFilters {
    commentFilter: 'all' | 'internal' | 'closingMeeting';
    todoStatus: 'open' | 'done';
    todoAudience: 'all' | 'todo' | 'reviewer' | 'client';
    searchTerm: string;
    sort: NoteSort | null;
}

/** AC5 — die Suche deckt ALLE Felder ab, nicht nur den Text. */
function matchesSearch(row: NoteRow, term: string): boolean {
    if (term.trim().length === 0) {
        return true;
    }
    const needle = term.trim().toLowerCase();
    return [
        row.text,
        row.accountNumber ?? '',
        row.accountName ?? '',
        row.author.name ?? '',
        row.createdLabel,
        row.dueLabel ?? '',
        ...row.tags,
    ]
        .join(' ')
        .toLowerCase()
        .includes(needle);
}

const SORTERS: Record<NoteSortKey, (a: NoteRow, b: NoteRow) => number> = {
    text: (a, b) => a.text.localeCompare(b.text, 'de-CH'),
    tags: (a, b) => a.tags.join(',').localeCompare(b.tags.join(','), 'de-CH'),
    // Nach NUMMER sortiert, nicht nach Bezeichnung: die Spalte wird nach der Nummer gelesen.
    account: (a, b) => (a.accountNumber ?? '').localeCompare(b.accountNumber ?? '', 'de-CH'),
    author: (a, b) => (a.author.name ?? '').localeCompare(b.author.name ?? '', 'de-CH'),
    created: (a, b) => swissToSortable(a.createdLabel).localeCompare(swissToSortable(b.createdLabel)),
    due: (a, b) => dueOrder(a).localeCompare(dueOrder(b)),
};

function swissToSortable(label: string): string {
    const parts = label.split('.');
    return parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : label;
}

/** ⚠️ Ohne Faelligkeit ZULETZT (AC6) — deshalb ein Sortierschluessel, der garantiert groesser ist. */
function dueOrder(row: NoteRow): string {
    return row.dueLabel === null || row.dueLabel === undefined ? '9999-99-99' : swissToSortable(row.dueLabel);
}

export function applyOverview(rows: NoteRow[], kind: 'COMMENT' | 'TODO', filters: OverviewFilters): NoteRow[] {
    let out = rows.filter((row) => row.kind === kind);

    if (kind === 'COMMENT') {
        if (filters.commentFilter !== 'all') {
            out = out.filter((row) => row.tags.indexOf(filters.commentFilter as never) !== -1);
        }
    } else {
        // ⚠️ Erst der Status, dann die Zielgruppe (AC3) — die zweite Reihe verfeinert die erste.
        out = out.filter((row) => (filters.todoStatus === 'done' ? !!row.done : !row.done));
        if (filters.todoAudience !== 'all') {
            out = out.filter((row) => row.tags.indexOf(filters.todoAudience as never) !== -1);
        }
    }

    out = out.filter((row) => matchesSearch(row, filters.searchTerm));

    if (filters.sort !== null) {
        const compare = SORTERS[filters.sort.key];
        const factor = filters.sort.direction === 'asc' ? 1 : -1;
        out = [...out].sort((a, b) => compare(a, b) * factor);
    } else {
        // Vorsortierung (ADR-0019 E7): Kommentare neueste zuerst, Pendenzen nach Faelligkeit
        // aufsteigend mit den fristlosen zuletzt.
        out =
            kind === 'COMMENT'
                ? [...out].sort((a, b) => SORTERS.created(b, a))
                : [...out].sort((a, b) => SORTERS.due(a, b));
    }
    return out;
}

// ── CSV ───────────────────────────────────────────────────────────────────────────────────────

/**
 * Die CSV der AKTUELLEN Ansicht (AC10, ADR-0019 E8) — im Browser, ohne Server.
 *
 * ⚠️ Die Zeilen kommen bereits gefiltert, gesucht und sortiert herein. Der Export darf die Auswahl
 * nicht selbst noch einmal treffen: „was ich sehe" und „was in der Datei steht" muessen dieselbe
 * Menge sein, sonst ist die Datei eine zweite Wahrheit.
 *
 * ⚠️ Details, die Excel sonst zerlegt: **BOM** (sonst zeigt Excel Umlaute als Mojibake), **CRLF**,
 * Semikolon als Trennzeichen (de-CH-Excel erwartet es), und jedes Feld in Anfuehrungszeichen mit
 * verdoppelten inneren. Eine leere Liste ergibt eine Datei MIT Kopfzeile, keinen Fehler (AC10).
 */
export function toCsv(rows: readonly NoteRow[], headers: readonly string[]): string {
    const cell = (value: string) => `"${value.split('"').join('""')}"`;
    const line = (values: readonly string[]) => values.map(cell).join(';');
    const body = rows.map((row) =>
        line([
            row.text,
            row.tags.join(', '),
            // Wie im Prototyp EIN Feld: `1022 PostFinance`. In der Tabelle zweizeilig, in der
            // Datei eine Spalte — eine CSV-Zelle mit Zeilenumbruch bricht Excel.
            [row.accountNumber ?? '', row.accountName ?? ''].filter((x) => x.length > 0).join(' '),
            row.author.name ?? '',
            row.createdLabel,
            row.dueLabel ?? '',
            row.done ? 'x' : '',
        ]),
    );
    return '﻿' + [line(headers), ...body].join('\r\n') + '\r\n';
}

/** Loest den Download aus. Getrennt von {@link toCsv}, damit der Inhalt ohne DOM testbar bleibt. */
export function downloadCsv(content: string, filename: string): void {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
}
